import { FetchResult } from './fetchDataWithToken';
import { fetchDataWithPartnerToken } from './fetchDataWithPartnerToken';

/**
 * Check if the target element is a link.
 * @param target - The target element.
 * @returns true if the target is a link, false otherwise.
 */
export function isLink(target: HTMLElement): boolean {
  return target.tagName === 'A' && target.hasAttribute('href');
}

/**
 * Check if the link should be intercepted.
 * @param target - The target element.
 * @param pathToIntercept - The path to intercept.
 * @returns true if the link should be intercepted, false otherwise.
 */
export const shouldIntercept = (
  element: HTMLAnchorElement,
  pattern: string,
): boolean => {
  const willIntercept = element.pathname.includes(pattern);
  return willIntercept;
};

/**
 * Get the absolute root URL.
 * @param rootUrl - The root URL.
 * @returns The absolute root URL.
 */
export function getAbsoluteRootUrl(rootUrl: string): string {
  return rootUrl.startsWith('http://') || rootUrl.startsWith('https://')
    ? rootUrl
    : window.location.origin + rootUrl;
}

/**
 * Open a new tab with the fetched HTML content and submit the form if present.
 * @param htmlContent - The HTML content to write in the new tab.
 */
export function openNewTabWithForm(htmlContent: string): void {
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.open();
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    newWindow.onload = () => {
      const form = newWindow.document.forms[0];
      if (form) {
        form.submit();
      }
    };
  }
}

export function setupNewWindow(htmlTitle = 'Loading...') {
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.open();
    newWindow.document.write(
      `<html><head><title>${htmlTitle}</title></head><body><div id="root"></div></body></html>`,
    );
    newWindow.document.close();
  }
  return newWindow;
}

export const resolveAbsoluteUrl = (href: string): string => {
  try {
    return href.startsWith('http')
      ? href
      : new URL(href, window.location.origin).href;
  } catch {
    throw new Error('Invalid URL format');
  }
};

export const openLoadingTab = (): Window | null => {
  const newTab = window.open('', '_blank');
  if (newTab) {
    newTab.document.write(
      '<html><head><title>Loading...</title></head><body>Loading...</body></html>',
    );
    newTab.document.close();
  }
  return newTab;
};

const renderErrorPage = (tab: Window, message: string): void => {
  tab.document.open();
  tab.document.write(
    `<html><head><title>Error</title></head><body><h4>${message}</h4><p>Please contact support.</p></body></html>`,
  );
  tab.document.close();
};

/**
 * Fetch data with appropriate token based on content type.
 * @param url - The URL to fetch data from.
 * @param contentType - The expected content type.
 * @param partnerId - The partner ID for PDF content (optional).
 * @returns Promise resolving to FetchResult.
 */
export const fetchDataWithAppropriateToken = async (
  url: string,
  contentType: 'text/html' | 'application/pdf',
  partnerId?: string,
): Promise<FetchResult> => {
  // For PDF content, use partner token if available
  if (contentType === 'application/pdf' && partnerId) {
    return fetchDataWithPartnerToken(url, partnerId);
  }

  // For HTML content or when no partner ID, use regular token
  const { fetchDataWithToken } = await import('./fetchDataWithToken');
  return fetchDataWithToken(url);
};

export const handleFetchResult = (
  tab: Window | null,
  result: FetchResult,
  expectedContentType: 'text/html' | 'application/pdf',
): void => {
  if (!tab) return;

  if (result.error) {
    renderErrorPage(
      tab,
      `Error: ${result.error.message} (Code: ${result.error.code})`,
    );
    return;
  }

  if (!result.content || !result.contentType) {
    renderErrorPage(tab, 'Missing content or content type');
    return;
  }

  if (result.contentType !== expectedContentType) {
    renderErrorPage(tab, `Unexpected content type: ${result.contentType}`);
    return;
  }

  if (
    result.contentType === 'text/html' &&
    typeof result.content === 'string'
  ) {
    tab.document.open();
    tab.document.write(result.content);
    tab.document.close();

    const localTab = tab;
    localTab.onload = () => {
      const form = localTab.document.forms[0];
      form?.submit();
    };
  } else if (
    result.contentType === 'application/pdf' &&
    result.content instanceof Blob
  ) {
    const blobUrl = URL.createObjectURL(result.content);
    const localTab = tab;
    localTab.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  }
};

export const openDecodedLink = (absoluteHref: string): void => {
  const urlParams = new URLSearchParams(new URL(absoluteHref).search);
  const webParam = urlParams.get('web');
  if (!webParam) return;

  const decodedWebUrl = decodeURIComponent(webParam).replace(
    /[^a-zA-Z0-9/:.]/g,
    '',
  );
  window.open(decodedWebUrl, '_blank');
};

/**
 * Check if a URL points to a PDF file.
 * @param url - The URL to check.
 * @returns true if the URL has a .pdf extension, false otherwise.
 */
export const isPdfUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.pathname.toLowerCase().endsWith('.pdf');
  } catch {
    // If URL parsing fails, check if the string ends with .pdf
    return url.toLowerCase().endsWith('.pdf');
  }
};
